import { clearBugsnagUser } from './bugsnag';

export interface AuthStorage {
  token?: string;
  user?: Api.UserObject;
  start_url?: string;
  code?: string;
  target?: string;
  language?: string;
}

/**
 * Gets the current auth data from localStorage
 */
export function getAuthFromStorage(): AuthStorage {
  try {
    const authData = localStorage.getItem('auth');
    return authData ? JSON.parse(authData) : {};
  } catch (error) {
    console.error('Error parsing auth data from localStorage:', error);
    return {};
  }
}

/**
 * Updates specific auth data in localStorage
 */
export function updateAuthInStorage(updates: Partial<AuthStorage>): void {
  const currentAuth = getAuthFromStorage();
  const updatedAuth = { ...currentAuth, ...updates };
  localStorage.setItem('auth', JSON.stringify(updatedAuth));
}

/**
 * Removes auth data from localStorage
 */
export function clearAuthFromStorage(): void {
  localStorage.removeItem('auth');
}

/**
 * Gets the user object from localStorage
 */
export function getUserFromStorage(): Api.UserObject | undefined {
  const auth = getAuthFromStorage();
  return auth.user;
}

/**
 * Updates the user object in localStorage
 */
export function updateUserInStorage(user: Api.UserObject): void {
  updateAuthInStorage({ user });
}

/**
 * Gets the token from localStorage
 */
export function getTokenFromStorage(): string | undefined {
  const auth = getAuthFromStorage();
  return auth.token;
}

/**
 * Updates the token in localStorage
 */
export function updateTokenInStorage(token: string): void {
  updateAuthInStorage({ token });
}

/**
 * Gets the start_url from localStorage
 */
export function getStartUrlFromStorage(): string | undefined {
  const auth = getAuthFromStorage();
  return auth.start_url;
}

/**
 * Updates the start_url in localStorage
 */
export function updateStartUrlInStorage(startUrl: string): void {
  updateAuthInStorage({ start_url: startUrl });
}

/**
 * Gets the target from localStorage
 */
export function getTargetFromStorage(): string | undefined {
  const auth = getAuthFromStorage();
  return auth.target;
}

/**
 * Updates the target in localStorage
 */
export function updateTargetInStorage(target: string): void {
  updateAuthInStorage({ target });
}

/**
 * Clears the target from localStorage
 */
export function clearTargetFromStorage(): void {
  const auth = getAuthFromStorage();
  delete auth.target;
  localStorage.setItem('auth', JSON.stringify(auth));
}

/**
 * Utility function for logout that can be used outside React components
 * For React components, use useLogout() hook instead
 */
export const logout = () => {
  clearAuthFromStorage();
  // Clear Bugsnag user when logging out
  clearBugsnagUser();
  window.location.href = '/login';
};

export const isJwtValid = (): boolean => {
  try {
    const token = getTokenFromStorage();
    if (!token) return false;

    // Check if token is expired using the existing checkTokenExpiry function
    // For now, we'll do a basic check - if the token exists and has a structure, consider it valid
    // The actual expiry check will be done by the backend when we use the token
    return true;
  } catch (error) {
    return false;
  }
};

export interface LoginDataType {
  email: string;
}
export interface SignupDataType {
  email: string;
  first_name: string;
  account_type: string;
}

/**
 * Gets the language from localStorage
 */
export function getLanguageFromStorage(): string | undefined {
  const auth = getAuthFromStorage();
  return auth.language;
}

/**
 * Updates the language in localStorage
 */
export function updateLanguageInStorage(language: string): void {
  updateAuthInStorage({ language });
}

export const createRequestBody = (
  email: string,
  first_name?: string,
  account_type?: string,
  target?: string
):
  | LoginDataType
  | SignupDataType
  | (LoginDataType & { target: string })
  | (SignupDataType & { target: string }) => {
  const baseBody =
    first_name !== undefined && account_type !== undefined
      ? { email: email.trim(), first_name, account_type }
      : { email: email.trim() };

  // Use target parameter if provided, otherwise try to get from localStorage
  const targetValue = target || getTargetFromStorage();

  if (targetValue) {
    return { ...baseBody, target: targetValue };
  }

  return baseBody;
};
